import { useCallback, useEffect, useState } from "react";
import { ArrowLeftRight, Search, SearchX } from "lucide-react";
import { useDolfinClient } from "@/components/DolfinProvider";
import { cardLabel } from "@/components/ExpensesTable";
import { formatCurrency, formatDate } from "@/lib/format";
import { PageContainer, PageHeader } from "@/components/PagePrimitives";
import { TransactionStatusBadge } from "@/components/ExpenseStatus";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  DateRangeFilter,
  EmptyState,
  ErrorState,
  HeaderRow,
  LoadingState,
  MerchantCell,
  Pager,
  ReceiptPresence,
  TH,
  TableShell,
  dateRangeQuery,
  prettyCategory,
  prettyMerchant,
  rowLink,
  type DateRange,
} from "@/components/ExpenseUi";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import type { components } from "@dolfin/api-public";

type CardTransactionResponse = components["schemas"]["CardTransactionResponse"];

const PAGE_SIZE = 20;

// Separate from the expenses list though one-to-one: this answers "did it reach us, and what did the network say".
export function CardTransactionsPage({ onSelect }: { onSelect?: (expenseId: string) => void } = {}) {
  const api = useDolfinClient();
  const [transactions, setTransactions] = useState<CardTransactionResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 400);
  const [dates, setDates] = useState<DateRange>({});
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  // One small request: an organisation has a handful of cards.
  const [cardNames, setCardNames] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    api.GET("/v1/card-connections", { params: { query: { IncludeRemoved: true } } }).then(({ data }) =>
      setCardNames(
        new Map(
          (data ?? []).map((c) => [c.id!, cardLabel({ cardNetwork: c.network, cardLast4: c.last4 })]),
        ),
      ),
    );
  }, [api]);

  const fetchTransactions = useCallback(
    async (cursor?: string) => {
      setLoading(true);
      setError(null);
      const query: Record<string, string | number> = { Limit: PAGE_SIZE };
      if (debouncedSearch.trim()) query.Search = debouncedSearch.trim();
      Object.assign(query, dateRangeQuery(dates));
      if (cursor) query.Cursor = cursor;

      const { data, error: apiError } = await api.GET("/v1/card-transactions", {
        params: { query },
      });

      if (apiError || !data) {
        setError("Failed to load card transactions");
      } else {
        setTransactions(data.data ?? []);
        setNextCursor(data.meta?.cursors?.next ?? null);
      }
      setLoading(false);
    },
    [api, debouncedSearch, dates],
  );

  useEffect(() => {
    setCursorStack([]);
    setNextCursor(null);
    fetchTransactions();
  }, [fetchTransactions]);

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Expenses"
        title="Card transactions"
        subtitle="Everything received from the card network, newest first. Each one becomes an expense automatically."
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by merchant"
            className="h-9 pl-8"
          />
        </div>
        <DateRangeFilter value={dates} onChange={setDates} />
      </div>

      {error ? (
        <ErrorState message={error} onRetry={() => fetchTransactions(cursorStack[cursorStack.length - 1])} />
      ) : loading ? (
        <LoadingState label="Loading transactions..." />
      ) : transactions.length === 0 ? (
        debouncedSearch.trim() || dates.from || dates.to ? (
          <EmptyState icon={SearchX} title="No transactions match">
            Nothing from the card network matches that search or those dates.
          </EmptyState>
        ) : (
          <EmptyState icon={ArrowLeftRight} title="No card transactions yet">
            Link a card and its transactions arrive here as the network reports them.
          </EmptyState>
        )
      ) : (
        <>
          <TableShell>
            <Table>
              <TableHeader>
                <HeaderRow>
                  <TableHead className={TH}>Merchant</TableHead>
                  <TableHead className={TH}>Card</TableHead>
                  <TableHead className={TH}>Occurred</TableHead>
                  <TableHead className={TH}>Status</TableHead>
                  <TableHead className={TH}>Receipt</TableHead>
                  <TableHead className={cn(TH, "text-right")}>Amount</TableHead>
                </HeaderRow>
              </TableHeader>
              <TableBody>
                {transactions.map((transaction) => (
                  <TableRow
                    key={transaction.id}
                    {...rowLink(
                      onSelect && transaction.expenseId ? () => onSelect(transaction.expenseId!) : undefined,
                    )}
                  >
                    <TableCell className="max-w-[320px]">
                      <MerchantCell
                        name={transaction.enrichedMerchantName || transaction.descriptor}
                        secondary={prettyCategory(transaction.mccLabel) ?? "Uncategorised"}
                      />
                      {/* What a person matches against their statement; hidden when it would only repeat the name above. */}
                      {transaction.descriptor &&
                      prettyMerchant(transaction.descriptor) !==
                        prettyMerchant(transaction.enrichedMerchantName || transaction.descriptor) ? (
                        <div className="mt-1 pl-11 font-mono text-[10px] text-muted-foreground/80">
                          {transaction.descriptor}
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm">
                      {cardNames.get(transaction.cardConnectionId!) ?? "-"}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <div className="text-sm text-muted-foreground">
                        {formatDate(transaction.transactionOccurrenceDate ?? transaction.ingestedAt)}
                      </div>
                      {transaction.acceptorCity || transaction.acceptorCountry ? (
                        <div className="text-xs text-muted-foreground capitalize">
                          {[transaction.acceptorCity?.toLowerCase(), transaction.acceptorCountry]
                            .filter(Boolean)
                            .join(", ")}
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <TransactionStatusBadge status={transaction.status} />
                    </TableCell>
                    <TableCell>
                      <ReceiptPresence attached={transaction.hasReceipt} />
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <div className="font-semibold tabular-nums">
                        {formatCurrency(transaction.effectiveAmount, transaction.transactionCurrency)}
                      </div>
                      {/* Merchant amount, then what the card was billed after FX: the billed figure reaches the statement. */}
                      {transaction.billingCurrency &&
                      transaction.billingCurrency !== transaction.transactionCurrency &&
                      transaction.status === "Settled" ? (
                        <div className="text-xs text-muted-foreground tabular-nums">
                          billed {formatCurrency(transaction.billingSettledAmount, transaction.billingCurrency)}
                        </div>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableShell>

          <Pager
            page={cursorStack.length + 1}
            hasPrevious={cursorStack.length > 0}
            hasNext={!!nextCursor}
            onPrevious={() => {
              const stack = [...cursorStack];
              stack.pop();
              setCursorStack(stack);
              fetchTransactions(stack[stack.length - 1]);
            }}
            onNext={() => {
              if (!nextCursor) return;
              setCursorStack((prev) => [...prev, nextCursor]);
              fetchTransactions(nextCursor);
            }}
          />
        </>
      )}
    </PageContainer>
  );
}
